import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-y bg-white">
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="overline mb-3">Nous contacter</p>
          <h2 className="text-3xl sm:text-4xl font-semibold text-secondary tracking-tight">
            Une question ? Parlons-en.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Info + Map */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="contact-phone-card"
                className="p-5 rounded-2xl border border-gray-100 hover:border-primary/30 hover:-translate-y-1 transition-all bg-white"
              >
                <Phone className="w-6 h-6 text-primary mb-3" />
                <div className="text-xs text-muted-foreground mb-1">Téléphone</div>
                <div className="font-semibold text-secondary">{COMPANY.phone}</div>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-card"
                className="p-5 rounded-2xl border border-gray-100 hover:border-primary/30 hover:-translate-y-1 transition-all bg-white"
              >
                <Mail className="w-6 h-6 text-primary mb-3" />
                <div className="text-xs text-muted-foreground mb-1">Courriel</div>
                <div className="font-semibold text-secondary text-sm break-all">{COMPANY.email}</div>
              </a>
              <div className="p-5 rounded-2xl border border-gray-100 bg-white">
                <MapPin className="w-6 h-6 text-primary mb-3" />
                <div className="text-xs text-muted-foreground mb-1">Zone desservie</div>
                <div className="font-semibold text-secondary">{COMPANY.city}</div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-gray-100 aspect-[16/12]">
              <iframe
                title="Carte Québec"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178384.11024195477!2d-71.42879825644531!3d46.81388100000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cb896b174321ea5%3A0x9d8d1e79fbd3ea5b!2sQu%C3%A9bec!5e0!3m2!1sfr!2sca!4v1701000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="p-8 rounded-2xl border border-gray-100 bg-gray-50/40 space-y-5"
          >
            <div>
              <Label htmlFor="c-name" className="text-secondary mb-2 block">Nom complet *</Label>
              <Input id="c-name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Votre nom" className="bg-white" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-email" className="text-secondary mb-2 block">Courriel *</Label>
                <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="vous@courriel.com" className="bg-white" />
              </div>
              <div>
                <Label htmlFor="c-phone" className="text-secondary mb-2 block">Téléphone</Label>
                <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="418 555-0123" className="bg-white" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-msg" className="text-secondary mb-2 block">Message *</Label>
              <Textarea id="c-msg" data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} placeholder="Comment pouvons-nous vous aider ?" className="bg-white" />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full bg-primary hover:bg-primary/90 rounded-xl py-6 text-base font-semibold"
            >
              {loading ? "Envoi..." : (<>Envoyer le message <Send className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
